import "./new.scss";
import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { centerInputs } from "../../formSource";
import { serverUrl } from "../../temp";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

const theme = createTheme({
  palette: {
    primary: {
      main: "#7451f8",
    },
    secondary: {
      main: "#6c757d",
    },
  },
});

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const FilePreviewContainer = styled('div')({
  position: 'relative',
  width: '100%',
  height: 200,
  borderRadius: 12,
  border: '2px dashed #e0e0e0',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#fafafa',
  marginBottom: 20,
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&.drag-active': {
    borderColor: '#7451f8',
    backgroundColor: 'rgba(116, 81, 248, 0.05)',
  },
});

const PreviewImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
});

const FileInfo = styled('div')({
  marginTop: 10,
  textAlign: 'center',
  '& .fileName': {
    fontWeight: 500,
    color: '#333',
    fontSize: 14,
    marginTop: 8,
    wordBreak: 'break-all',
    padding: '0 10px',
  },
  '& .fileSize': {
    color: '#757575',
    fontSize: 12,
  },
});

const RemoveButton = styled(Button)({
  position: 'absolute',
  top: 8,
  right: 8,
  minWidth: 30,
  height: 30,
  borderRadius: '50%',
  padding: 0,
});

const CenterNew = ({ title }) => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [inputValues, setInputValues] = useState({
    status: 1, // Default status to active
  });
  const token = localStorage.getItem("token");

  const redirectToLogin = () => {
    toast.warning("Please login first to access this page", {
      autoClose: 2000,
      onClose: () => (window.location.href = "/"),
    });
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const selectedFile = e.dataTransfer.files[0];
      processFile(selectedFile);
    }
  }, []);

  const processFile = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type and size (max 2MB for logos)
    if (!selectedFile.type.startsWith("image/")) {
      toast.error("Please upload an image file (JPG, PNG)");
      return;
    }

    if (selectedFile.size > 2 * 1024 * 1024) {
      toast.error("Image size exceeds 2MB limit");
      return;
    }

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (e) => {
      setFilePreview(e.target.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFilePreview("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputValues({
      ...inputValues,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error("Please upload a logo for the center");
      return;
    }

    const formData = new FormData();
    formData.append("name", inputValues.name);
    formData.append("address", inputValues.address);
    formData.append("phone_no", inputValues.phone_no);
    formData.append("status", inputValues.status);
    formData.append("logo", file);

    const toastId = toast.loading("Creating new center...");

    try {
      const response = await fetch(`${serverUrl}/add_center`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.code >= 400) {
        toast.update(toastId, {
          render: data.message || "Failed to create center",
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });

        if (typeof data.message === "object") {
          Object.entries(data.message).forEach(([field, errors]) => {
            errors.forEach((errMsg) => {
              toast.error(`${field}: ${errMsg}`);
            });
          });
        }
      } else {
        toast.update(toastId, {
          render: data.message || "Center created successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });

        // Reset the form
        setFile(null);
        setFilePreview("");
        setInputValues({
          status: 1,
        });

        setTimeout(() => {
          navigate("/center");
        }, 1500);
      }
    } catch (error) {
      console.log(error);
      toast.update(toastId, {
        render: "Error occurred while creating center",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <ToastContainer position="top-right" autoClose={3000} />
      {!token && redirectToLogin()}
      {token && (
        <div className="new">
          <Sidebar />
          <div className="newContainer">
            <Navbar />
            <div className="contentWrapper">
              <Card className="formCard">
                <CardContent className="cardContent">
                  <Typography
                    variant="h5"
                    component="h2"
                    gutterBottom
                    className="formTitle"
                  >
                    {title}
                  </Typography>
                  <form onSubmit={handleSubmit} className="responsiveForm">
                    <Grid container spacing={2}>
                      <Grid item xs={12} className="formGridItem">
                        <FilePreviewContainer
                          className={dragActive ? "drag-active" : ""}
                          onDragEnter={handleDrag}
                          onDragOver={handleDrag}
                          onDragLeave={handleDrag}
                          onDrop={handleDrop}
                        >
                          {filePreview ? (
                            <>
                              <PreviewImage
                                src={filePreview}
                                alt="Center Logo Preview"
                              />
                              {file && (
                                <>
                                  <FileInfo>
                                    <div className="fileName">{file.name}</div>
                                    <div className="fileSize">
                                      {(file.size / 1024).toFixed(1)} KB
                                    </div>
                                  </FileInfo>
                                  <RemoveButton
                                    variant="contained"
                                    color="error"
                                    onClick={handleRemoveFile}
                                  >
                                    ×
                                  </RemoveButton>
                                </>
                              )}
                            </>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                p: 2,
                              }}
                            >
                              <CloudUploadIcon
                                sx={{ fontSize: 48, color: dragActive ? '#7451f8' : '#bdbdbd', mb: 1, transition: 'color 0.3s' }}
                              />
                              <Typography variant="body1" color="textSecondary">
                                {dragActive ? "Drop your logo here" : "Drag & drop your logo here or"}
                              </Typography>
                              <Button
                                component="label"
                                variant="outlined"
                                startIcon={<DriveFolderUploadOutlinedIcon />}
                                sx={{ mt: 2 }}
                              >
                                Browse Files
                                <VisuallyHiddenInput
                                  type="file"
                                  accept="image/*"
                                  onChange={handleFileChange}
                                  required
                                />
                              </Button>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ mt: 1 }}
                              >
                                (Supports JPG, PNG up to 2MB)
                              </Typography>
                            </Box>
                          )}
                        </FilePreviewContainer>
                      </Grid>

                      {centerInputs.map((input) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          key={input.id}
                          className="formGridItem"
                        >
                          <div className="formInput">
                            {input.fieldName === "status" ? null : (
                              <>
                                <label>{input.label}</label>
                                {input.type === "dropdown" ? (
                                  <select
                                    name={input.fieldName}
                                    value={inputValues[input.fieldName] || ""}
                                    onChange={handleInputChange}
                                    className="formSelect"
                                    required
                                  >
                                    {input.options.map((option) => (
                                      <option key={option} value={option}>
                                        {option}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type={input.type}
                                    placeholder={input.placeholder}
                                    name={input.fieldName}
                                    value={inputValues[input.fieldName] || ""}
                                    onChange={handleInputChange}
                                    required={input.fieldName !== "status"}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </Grid>
                      ))}
                    </Grid>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 3,
                        mt: 4,
                        pb: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={() => navigate("/center")}
                        sx={{ minWidth: "120px", height: "42px" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: "150px", height: "42px" }}
                        // disabled={!file}
                      >
                        Create Center
                      </Button>
                    </Box>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </ThemeProvider>
  );
};

export default CenterNew;